import { AccountStatus } from "@/generated/model/accountStatus";
import type { EmployeeKeyInformationDto } from "@/generated/model/employeeKeyInformationDto";
import type { EmployeeLoginAndSecurityDto } from "@/generated/model/employeeLoginAndSecurityDto";
import {
  accountStatusLabel,
  employmentStatusLabel,
  formatProfileDate,
  isFormerEmployee,
  NOT_RECORDED,
  textOrNotRecorded,
} from "@/lib/people";
import { nurseryRoleLabel } from "@/lib/roles";
import { cn } from "@/lib/utils";

interface ProfileStatusStripProps {
  keyInformation: EmployeeKeyInformationDto;
  /** Null when the viewer may not see Login and Security; Account is then left out. */
  loginAndSecurity: EmployeeLoginAndSecurityDto | null;
}

export function ProfileStatusStrip({
  keyInformation,
  loginAndSecurity,
}: ProfileStatusStripProps) {
  const employed =
    keyInformation.employmentStatus !== null &&
    !isFormerEmployee(keyInformation.employmentStatus);

  return (
    <dl
      data-testid="profile-status-strip"
      className="ph-no-capture sentry-block grid w-full grid-cols-2 gap-4 rounded-md border border-solid border-neutral-200 p-4 md:grid-cols-3 xl:grid-cols-6"
    >
      <StatusItem
        label="Employment status"
        value={employmentStatusLabel(keyInformation.employmentStatus)}
        positive={employed}
      />
      <StatusItem
        label="Start date"
        value={formatProfileDate(keyInformation.startDate)}
        phoneHidden={true}
      />
      <StatusItem
        label="Role"
        value={nurseryRoleLabel(keyInformation.nurseryRole) || NOT_RECORDED}
        phoneHidden={true}
      />
      <StatusItem
        label="Room / area"
        value={textOrNotRecorded(keyInformation.room?.name)}
        phoneHidden={true}
      />
      <StatusItem
        label="Line manager"
        value={textOrNotRecorded(keyInformation.lineManager?.name)}
        phoneHidden={true}
      />
      {loginAndSecurity && (
        <StatusItem
          label="Account"
          value={accountStatusLabel(loginAndSecurity.accountStatus)}
          positive={loginAndSecurity.accountStatus === AccountStatus.ACTIVE}
          testId="profile-status-account"
        />
      )}
    </dl>
  );
}

function StatusItem({
  label,
  value,
  positive = false,
  phoneHidden = false,
  testId,
}: {
  label: string;
  value: string;
  positive?: boolean;
  phoneHidden?: boolean;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "flex min-w-0 flex-col gap-0.5",
        phoneHidden && "hidden md:flex",
      )}
    >
      <dt className="text-caption font-caption text-subtext-color">{label}</dt>
      <dd
        className={cn(
          "text-body-bold font-body-bold break-words",
          positive ? "text-success-700" : "text-default-font",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
